import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPatch, apiPost } from "@/lib/apiClient";
import { useOneOrgContext } from "@/auth/OneOrgContext";
import { useSession } from "@/auth/SessionProvider";
import {
  articleBelongsToKnowledgeBase,
  getActiveKnowledgeBaseId,
  knowledgeBaseAnchorMarkdown,
  kbMembershipKey,
  listKnowledgeBases,
  mergeDiscoveredKnowledgeBases,
  removeKnowledgeBase,
  KNOWLEDGE_BASE_ANCHOR_MARKER,
  KNOWLEDGE_BASES_CHANGED,
  setActiveKnowledgeBaseId,
  type KnowledgeBaseRecord,
  withoutKbMembership,
} from "@/lib/knowledgeBaseRegistry";
import { clearCustomBooksForKnowledgeBase } from "@/lib/knowledgeBookRegistry";
import { clearCustomLibrariesForKnowledgeBase } from "@/lib/knowledgeLibraryRegistry";
import type { KnowledgeArticle } from "@/lib/knowledgeModel";
import { resolveKnowledgeOrgId } from "@/lib/knowledgeOrgId";
import {
  apiCreateKnowledgeBase,
  apiDeleteKnowledgeBase,
  apiSetActiveKnowledgeBase,
  apiUpdateKnowledgeBase,
} from "@/lib/knowledgeRegistryApi";
import { refreshKnowledgeRegistryFromApi } from "@/lib/knowledgeRegistryCrossSync";
import {
  pinKnowledgeBaseActiveSelection,
  pinnedKnowledgeBaseActiveId,
  reconcileKnowledgeBaseActivePin,
} from "@/lib/knowledgeActiveSelection";

type ArticlesResponse = {
  articles?: KnowledgeArticle[];
  error?: string;
};

export function useKnowledgeBases() {
  const session = useSession();
  const { orgContextStatus } = useOneOrgContext();
  const orgId = resolveKnowledgeOrgId(session.data?.user?.orgId);
  const [bases, setBases] = useState<KnowledgeBaseRecord[]>(() =>
    orgId ? listKnowledgeBases(orgId) : [],
  );
  const [activeId, setActiveId] = useState<string>(() =>
    orgId ? getActiveKnowledgeBaseId(orgId) : "",
  );

  const reloadLocal = useCallback(() => {
    const nextBases = listKnowledgeBases(orgId);
    setBases(nextBases);
    const isValid = (id: string) =>
      Boolean(id.trim()) && nextBases.some((base) => base.id === id.trim());
    const pinned = pinnedKnowledgeBaseActiveId(orgId, isValid);
    setActiveId(pinned || getActiveKnowledgeBaseId(orgId));
  }, [orgId]);

  const reload = useCallback(async () => {
    if (orgContextStatus === "loading") return;
    if (orgContextStatus === "missing" || !orgId) return;
    await refreshKnowledgeRegistryFromApi(orgId);
    reloadLocal();
  }, [orgContextStatus, orgId, reloadLocal]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    function onChange() {
      reloadLocal();
    }
    window.addEventListener(KNOWLEDGE_BASES_CHANGED, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(KNOWLEDGE_BASES_CHANGED, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [reloadLocal]);

  const active = useMemo(
    () => bases.find((base) => base.id === activeId) || bases[0] || null,
    [bases, activeId],
  );

  const setActive = useCallback(
    (kbId: string) => {
      if (!orgId) return;
      const id = kbId.trim();
      if (!id || id === activeId) return;
      pinKnowledgeBaseActiveSelection(orgId, id);
      setActiveKnowledgeBaseId(orgId, id);
      setActiveId(id);
      void apiSetActiveKnowledgeBase(id).then((res) => {
        if (res.ok) {
          reconcileKnowledgeBaseActivePin(orgId, res.data?.activeBaseId || id);
          return;
        }
        reloadLocal();
      });
    },
    [orgId, activeId, reloadLocal],
  );

  const mergeFromArticles = useCallback(
    (articles: { title?: string; collectionKeys: string[]; createdAt?: string; updatedAt?: string }[]) => {
      const result = mergeDiscoveredKnowledgeBases(orgId, articles);
      if (result.changed) reloadLocal();
    },
    [orgId, reloadLocal],
  );

  const createBase = useCallback(
    async (
      name: string,
      description?: string,
    ): Promise<
      | { ok: true; base: KnowledgeBaseRecord; articleId?: string }
      | { ok: false; error: string }
    > => {
      const created = await apiCreateKnowledgeBase({ name, description });
      if (!created.ok || !created.data?.base) {
        const detail = (created.error || "").trim();
        return {
          ok: false,
          error:
            detail ||
            (created.status
              ? `Could not create knowledge base (HTTP ${created.status}).`
              : "Could not create knowledge base."),
        };
      }
      const base = created.data.base;
      if (orgId) {
        pinKnowledgeBaseActiveSelection(orgId, base.id);
        setActiveKnowledgeBaseId(orgId, base.id);
        setActiveId(base.id);
        const activated = await apiSetActiveKnowledgeBase(base.id);
        if (activated.ok) {
          reconcileKnowledgeBaseActivePin(orgId, activated.data?.activeBaseId || base.id);
        }
        await refreshKnowledgeRegistryFromApi(orgId);
        reloadLocal();
      }

      const res = await apiPost<KnowledgeArticle>("/api/knowledge/articles", {
        title: base.name,
        markdown: knowledgeBaseAnchorMarkdown(base.name),
        status: "draft",
        collectionKeys: [kbMembershipKey(base.id)],
      });

      if (!res.ok || !res.data?.id) {
        return { ok: true, base };
      }

      return { ok: true, base, articleId: res.data.id };
    },
    [orgId, reloadLocal],
  );

  const editBase = useCallback(
    async (
      kbId: string,
      name: string,
      description?: string,
    ): Promise<{ ok: true; base: KnowledgeBaseRecord } | { ok: false; error: string }> => {
      const updated = await apiUpdateKnowledgeBase(kbId, { name, description });
      if (!updated.ok || !updated.data?.base) {
        return { ok: false, error: updated.error || "Could not update knowledge base." };
      }
      if (orgId) {
        await refreshKnowledgeRegistryFromApi(orgId);
        reloadLocal();
      }

      const list = await apiGet<ArticlesResponse>("/api/knowledge/articles?limit=500");
      if (list.ok && Array.isArray(list.data?.articles)) {
        for (const article of list.data.articles) {
          if (!articleBelongsToKnowledgeBase(article, updated.data.base.id)) continue;
          if (!(article.markdown || "").includes(KNOWLEDGE_BASE_ANCHOR_MARKER)) continue;
          await apiPatch<KnowledgeArticle>(
            `/api/knowledge/articles/${encodeURIComponent(article.id)}`,
            {
              title: updated.data.base.name,
              markdown: knowledgeBaseAnchorMarkdown(updated.data.base.name),
            },
          );
        }
      }

      return { ok: true, base: updated.data.base };
    },
    [orgId, reloadLocal],
  );

  const deleteBase = useCallback(
    async (
      kbId: string,
    ): Promise<{ ok: true; stripped: number; failed: number } | { ok: false; error: string }> => {
      const id = kbId.trim();
      if (!id) return { ok: false, error: "Knowledge base not found." };
      if (!listKnowledgeBases(orgId).some((base) => base.id === id)) {
        return { ok: false, error: "Knowledge base not found." };
      }

      const list = await apiGet<ArticlesResponse>("/api/knowledge/articles?limit=500");
      if (!list.ok) {
        return { ok: false, error: list.error || "Could not load articles to update membership." };
      }
      const rows = Array.isArray(list.data?.articles) ? list.data.articles : [];
      const members = rows.filter((article) => articleBelongsToKnowledgeBase(article, id));

      let stripped = 0;
      let failed = 0;
      for (const article of members) {
        const nextKeys = withoutKbMembership(article.collectionKeys, id);
        const res = await apiPatch<KnowledgeArticle>(
          `/api/knowledge/articles/${encodeURIComponent(article.id)}`,
          { collectionKeys: nextKeys },
        );
        if (res.ok) stripped += 1;
        else failed += 1;
      }

      if (failed > 0 && stripped === 0) {
        return {
          ok: false,
          error: "Could not remove knowledge base membership from articles. Nothing was deleted.",
        };
      }

      const removed = await apiDeleteKnowledgeBase(id);
      if (!removed.ok) {
        return { ok: false, error: removed.error || "Could not delete knowledge base." };
      }
      // Drop local cache before refresh so sync cannot re-upload the deleted base.
      removeKnowledgeBase(orgId, id);
      clearCustomLibrariesForKnowledgeBase(orgId, id);
      clearCustomBooksForKnowledgeBase(orgId, id);
      if (orgId) {
        await refreshKnowledgeRegistryFromApi(orgId, { skipLocalPush: true });
        reloadLocal();
      }

      if (failed > 0) {
        return { ok: true, stripped, failed };
      }
      return { ok: true, stripped, failed: 0 };
    },
    [orgId, reloadLocal],
  );

  return {
    bases,
    hasBases: bases.length > 0,
    activeId,
    active,
    orgId,
    setActive,
    createBase,
    editBase,
    deleteBase,
    mergeFromArticles,
    reload,
  };
}
